'use client';

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';
import { logger } from '@/lib/platform/utils/logger';

// Theme preference types
export type ThemePreference = 'light' | 'dark' | 'system';

interface DarkModeContextType {
  darkMode: boolean;
  themePreference: ThemePreference;
  setThemePreference: (preference: ThemePreference) => void;
  toggleDarkMode: () => void;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

// Helper: Get system theme preference
const getSystemTheme = (): boolean => {
  if (typeof window === 'undefined') return false; // SSR default: light
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

// Helper: Resolve theme preference to actual dark mode state
const resolveTheme = (preference: ThemePreference): boolean => {
  if (preference === 'system') {
    return getSystemTheme();
  }
  return preference === 'dark';
};

// Helper: Load theme preference from localStorage (synchronous)
const loadThemePreference = (): ThemePreference => {
  if (typeof window === 'undefined') return 'system'; // SSR default

  try {
    // Try to get saved preference from localStorage (correct key: 'user:theme')
    const theme = localStorage.getItem('user:theme') as ThemePreference | null;

    if (theme === 'dark' || theme === 'light' || theme === 'system') {
      return theme;
    }

    // Fallback: Check legacy 'themePreference' key
    const legacyTheme = localStorage.getItem('themePreference') as ThemePreference | null;
    if (legacyTheme === 'dark' || legacyTheme === 'light' || legacyTheme === 'system') {
      // Migrate to new key
      localStorage.setItem('user:theme', legacyTheme);
      localStorage.removeItem('themePreference');
      return legacyTheme;
    }

    // Also check legacy 'darkMode' boolean key
    const legacyDarkMode = localStorage.getItem('darkMode');
    if (legacyDarkMode === 'true') {
      localStorage.setItem('user:theme', 'dark');
      localStorage.removeItem('darkMode');
      return 'dark';
    }
    if (legacyDarkMode === 'false') {
      localStorage.setItem('user:theme', 'light');
      localStorage.removeItem('darkMode');
      return 'light';
    }
  } catch {
    // Fallback to system preference if localStorage fails
  }

  // Default: use system preference
  return 'system';
};

// Helper: Save theme preference to localStorage
const saveThemePreference = (preference: ThemePreference): void => {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem('user:theme', preference);
  } catch (error) {
    logger.error('[DarkMode] Failed to save theme preference:', error);
  }
};

// Helper: Get initial dark mode value synchronously (client-side only)
const getInitialDarkMode = (): boolean => {
  if (typeof window === 'undefined') return false; // SSR: default light

  // CRITICAL: Check DOM first (blocking script may have already applied theme)
  // This prevents hydration mismatch and re-renders
  const htmlElement = document.documentElement;
  const hasDarkClass = htmlElement.classList.contains('dark');

  // If blocking script already applied theme, trust the DOM
  if (hasDarkClass) return true;

  // Otherwise, resolve from localStorage/system preference
  const preference = loadThemePreference();
  return resolveTheme(preference);
};

export function DarkModeProvider({ children }: { children: ReactNode }) {
  // IMPORTANT: The first client render MUST match the server render (light/'system')
  // to avoid React hydration mismatches. The real theme is applied in the mount
  // effect below, immediately after hydration.
  const [themePreference, setThemePreferenceState] = useState<ThemePreference>('system');
  const [darkMode, setDarkMode] = useState(false);

  // Track if this is the first render (to skip initial useEffect run)
  const isFirstRender = useRef(true);

  // After hydration: load the real theme from DOM/localStorage
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time post-hydration sync from DOM/localStorage (external system)
    setThemePreferenceState(loadThemePreference());

    setDarkMode(getInitialDarkMode());
  }, []);

  // IMPORTANT: Do NOT use useLayoutEffect to apply theme!
  // The blocking script in layout.tsx handles theme application BEFORE React hydrates.
  // useLayoutEffect would cause flash/double-render because it runs AFTER first render.
  // We only need to listen for changes AFTER initial render (user toggling theme).

  // Apply theme when user manually changes preference (after initial render)
  useEffect(() => {
    // Skip on first render - blocking script in layout.tsx already applied theme
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (typeof window === 'undefined') return;

    const htmlElement = document.documentElement;
    const pathname = window.location.pathname;
    const urlParams = new URLSearchParams(window.location.search);
    const urlTheme = urlParams.get('theme');

    // Trust-center with URL theme parameter → force theme from URL
    if (pathname.includes('/trust-center') && (urlTheme === 'dark' || urlTheme === 'light')) {
      const shouldBeDark = urlTheme === 'dark';
      // eslint-disable-next-line react-hooks/set-state-in-effect -- syncs theme from URL/DOM (external system)
      setDarkMode(shouldBeDark);
      if (shouldBeDark) {
        htmlElement.classList.add('dark');
      } else {
        htmlElement.classList.remove('dark');
      }
      return;
    }

    // All other pages → use user preference
    const resolved = resolveTheme(themePreference);

    setDarkMode(resolved);
    if (resolved) {
      htmlElement.classList.add('dark');
    } else {
      htmlElement.classList.remove('dark');
    }
  }, [themePreference]); // Only trigger when user changes preference

  // Listen to system theme changes when preference is 'system'
  useEffect(() => {
    if (typeof window === 'undefined' || themePreference !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      setDarkMode(mediaQuery.matches);
      // Keep the Tailwind `dark:` variant in sync too — components styled with
      // dark: classes (e.g. the AI chat panel) read the .dark class on <html>,
      // not the React state, and would otherwise be stuck in the old theme
      // until the next full page load.
      document.documentElement.classList.toggle('dark', mediaQuery.matches);
    };
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [themePreference]);

  // Set theme preference (saves to localStorage)
  const setThemePreference = useCallback((preference: ThemePreference) => {
    logger.log('[DarkMode] Setting theme preference:', preference);
    setThemePreferenceState(preference);
    saveThemePreference(preference);
  }, []);

  // Toggle through: light → dark → system → light
  const toggleDarkMode = useCallback(() => {
    logger.log('[DarkMode] Toggle called, current preference:', themePreference);

    const nextPreference: ThemePreference =
      themePreference === 'light' ? 'dark' : themePreference === 'dark' ? 'system' : 'light';

    setThemePreference(nextPreference);
  }, [themePreference, setThemePreference]);

  const contextValue = useMemo(
    () => ({ darkMode, themePreference, setThemePreference, toggleDarkMode }),
    [darkMode, themePreference, setThemePreference, toggleDarkMode]
  );

  return <DarkModeContext.Provider value={contextValue}>{children}</DarkModeContext.Provider>;
}

export function useDarkMode() {
  const context = useContext(DarkModeContext);
  if (context === undefined) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
}
